import React, { useState, useEffect, useRef } from "react";
import {
  Shield,
  Activity,
  Users,
  Eye,
  Calendar,
  ChevronLeft,
  ChevronRight,
  Database,
  Info,
  Code2,
  User,
  Copy,
  CheckCircle,
  TrendingUp,
  Globe,
  Monitor,
  Smartphone,
  X,
} from "lucide-react";
import { toast } from "react-hot-toast";
import Loader from "../../widgets/Loader";
import Tooltip from "../../widgets/ToolTip";
import { MetricCard, MetricCardGrid } from "../../widgets/MetricCard";
import {
  userActivityReportService,
  UserActivityReport,
  UserActivityReportListParams,
} from "../../services/userActivityReport.service";
import UserActivityFilters from "../../components/reports/UserActivityFilters";

const UserActivityReports: React.FC = () => {
  // State management
  const [reports, setReports] = useState<UserActivityReport[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize] = useState(10);

  // Statistics State
  const [stats, setStats] = useState({
    total_events: 0,
    today_events: 0,
    unique_users: 0,
    most_common_event: "N/A",
  });
  const [isLoadingStats, setIsLoadingStats] = useState(true);

  // Filters State
  const [filters, setFilters] = useState<UserActivityReportListParams>({
    skip: 0,
    limit: 10,
  });

  // Event Details Modal state
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedReport, setSelectedReport] = useState<UserActivityReport | null>(null);
  const [activeTab, setActiveTab] = useState<"overview" | "metadata">("overview");
  const [copied, setCopied] = useState(false);

  const isInitialMount = useRef(true);

  // Fetch reports function
  const fetchReports = async () => {
    try {
      setIsLoading(true);
      const response = await userActivityReportService.getUserActivityReports(filters);
      setReports(response.user_activity_reports);
      setTotalCount(response.total_count);
    } catch (error) {
      console.error("Error fetching user activity reports:", error);
      toast.error("Failed to load user activity reports");
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch statistics function
  const fetchStats = async () => {
    try {
      setIsLoadingStats(true);
      const response = await userActivityReportService.getUserActivityReports({
        skip: 0,
        limit: 1000,
      });

      const allLogs = response.user_activity_reports;

      // Calculate today's logs
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const todayLogsCount = allLogs.filter(
        (log) => new Date(log.event_time) >= today
      ).length;

      // Calculate unique users
      const uniqueUsersSet = new Set(
        allLogs.map((log) => log.user_id || log.action_by).filter(Boolean)
      );
      const uniqueUsersCount = uniqueUsersSet.size;

      // Calculate most common event
      const eventCounts = allLogs.reduce((acc, log) => {
        acc[log.event_type] = (acc[log.event_type] || 0) + 1;
        return acc;
      }, {} as Record<string, number>);

      const mostCommonEvent = Object.entries(eventCounts).reduce(
        (max, [event, count]) => (count > max.count ? { event, count } : max),
        { event: "N/A", count: 0 }
      ).event;

      setStats({
        total_events: response.total_count,
        today_events: todayLogsCount,
        unique_users: uniqueUsersCount,
        most_common_event:
          mostCommonEvent !== "N/A"
            ? mostCommonEvent
              .replace(/_/g, " ")
              .toLowerCase()
              .replace(/\b\w/g, (l) => l.toUpperCase())
            : "N/A",
      });
    } catch (error) {
      console.error("Error fetching user activity stats:", error);
    } finally {
      setIsLoadingStats(false);
    }
  };

  // Fetch data on filters/mount
  useEffect(() => {
    if (isInitialMount.current) {
      fetchReports();
      fetchStats();
      isInitialMount.current = false;
    } else {
      const debounceTimer = setTimeout(() => {
        fetchReports();
      }, 300);
      return () => clearTimeout(debounceTimer);
    }
  }, [filters]);

  // Handle filters change
  const handleFiltersChange = (newFilters: UserActivityReportListParams) => {
    setFilters(newFilters);
    setCurrentPage(1);
  };

  // Pagination handler
  const handlePageChange = (page: number) => {
    const newFilters = {
      ...filters,
      skip: (page - 1) * pageSize,
    };
    setFilters(newFilters);
    setCurrentPage(page);
  };

  // View report details
  const handleViewDetails = (report: UserActivityReport) => {
    setSelectedReport(report);
    setShowDetailModal(true);
    setActiveTab("overview");
    setCopied(false);
  };

  // Close detail modal
  const handleCloseDetail = () => {
    setShowDetailModal(false);
    setSelectedReport(null);
    setActiveTab("overview");
    setCopied(false);
  };

  // Format date helper
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString("en-GB", {
      timeZone: "Asia/Kolkata",
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

  // Get event type color
  const getEventTypeColor = (eventType: string) => {
    const colors = {
      USER_CREATED: "bg-green-100 text-green-800 border border-green-200",
      USER_UPDATED: "bg-yellow-100 text-yellow-800 border border-yellow-200",
      USER_DELETED: "bg-red-100 text-red-800 border border-red-200",
      GROUP_CREATED: "bg-purple-100 text-purple-800 border border-purple-200",
      GROUP_UPDATED: "bg-indigo-100 text-indigo-800 border border-indigo-200",
      GROUP_DELETED: "bg-orange-100 text-orange-800 border border-orange-200",
      ROLE_CREATED: "bg-blue-100 text-blue-800 border border-blue-200",
      ROLE_UPDATED: "bg-cyan-100 text-cyan-800 border border-cyan-200",
      ROLE_DELETED: "bg-pink-100 text-pink-800 border border-pink-200",
      TOKEN_CREATED: "bg-teal-100 text-teal-800 border border-teal-200",
      TOKEN_DELETED: "bg-gray-100 text-gray-800 border border-gray-200",
      USER_LOGIN: "bg-emerald-100 text-emerald-800 border border-emerald-200",
      LOGIN: "bg-emerald-100 text-emerald-800 border border-emerald-200",
      LOGIN_FAILED: "bg-rose-100 text-rose-800 border border-rose-200",
      LOGOUT: "bg-slate-100 text-slate-800 border border-slate-200",
      REVOKE_OTHER_SESSIONS: "bg-amber-100 text-amber-800 border border-amber-200",
      REVOKE_ALL_SESSIONS: "bg-rose-100 text-rose-800 border border-rose-200",
      TEMPLATE_CREATED: "bg-emerald-100 text-emerald-800 border border-emerald-200",
      TEMPLATE_UPDATED: "bg-amber-100 text-amber-800 border border-amber-200",
      TEMPLATE_DELETED: "bg-rose-100 text-rose-800 border border-rose-200",
    };
    return (
      colors[eventType as keyof typeof colors] || "bg-gray-100 text-gray-800 border border-gray-200"
    );
  };

  // Predefined event types for filter dropdown
  const eventTypes = [
    { value: "USER_CREATED", label: "User Created" },
    { value: "USER_UPDATED", label: "User Updated" },
    { value: "USER_DELETED", label: "User Deleted" },
    { value: "GROUP_CREATED", label: "Group Created" },
    { value: "GROUP_DELETED", label: "Group Deleted" },
    { value: "GROUP_UPDATED", label: "Group Updated" },
    { value: "ROLE_CREATED", label: "Role Created" },
    { value: "ROLE_UPDATED", label: "Role Updated" },
    { value: "ROLE_DELETED", label: "Role Deleted" },
    { value: "TOKEN_CREATED", label: "Token Created" },
    { value: "TOKEN_DELETED", label: "Token Deleted" },
    { value: "TEMPLATE_CREATED", label: "Template Created" },
    { value: "TEMPLATE_UPDATED", label: "Template Updated" },
    { value: "TEMPLATE_DELETED", label: "Template Deleted" },
  ];

  const totalPages = Math.ceil(totalCount / pageSize);
  const startItem = totalCount === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const endItem = Math.min(currentPage * pageSize, totalCount);

  return (
    <div className="min-h-screen bg-gray-50">
      <div>
        {/* Header */}
        <div className="sticky top-0 z-40 bg-white border-slate-200 border-b mb-4">
          <div className="px-6 py-4">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div>
                <h1 className="text-xl font-bold text-gray-900">
                  User Activity Reports
                </h1>
                <p className="text-xs text-slate-600">
                  Monitor and analyze user activities, login events, and session history
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Metric Cards */}
        <MetricCardGrid>
          <MetricCard
            title="Total Events"
            value={isLoadingStats ? "..." : stats.total_events}
            icon={Database}
            iconColor="text-blue-600"
            iconBgColor="bg-blue-100"
          />
          <MetricCard
            title="Today's Events"
            value={isLoadingStats ? "..." : stats.today_events}
            icon={Activity}
            iconColor="text-green-600"
            iconBgColor="bg-green-100"
          />
          <MetricCard
            title="Unique Users"
            value={isLoadingStats ? "..." : stats.unique_users}
            icon={Users}
            iconColor="text-purple-600"
            iconBgColor="bg-purple-100"
          />
          <MetricCard
            title="Top Event"
            value={isLoadingStats ? "..." : stats.most_common_event}
            icon={TrendingUp}
            iconColor="text-orange-600"
            iconBgColor="bg-orange-100"
          />
        </MetricCardGrid>

        {/* Filters */}
        <UserActivityFilters
          onFiltersChange={handleFiltersChange}
          eventTypes={eventTypes}
          isLoading={isLoading}
          onRefresh={() => {
            fetchReports();
            fetchStats();
          }}
        />

        {/* Reports Table */}
        {isLoading ? (
          <div className="flex flex-col items-center justify-center min-h-[400px]">
            <Loader />
          </div>
        ) : reports.length > 0 ? (
          <>
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden mb-6">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Event
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        User
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Time
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        IP Address
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Device
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {reports.map((report) => (
                      <tr key={report._id} className="hover:bg-gray-50">
                        {/* Event */}
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="w-10 h-10 bg-gradient-to-br from-blue-100 to-purple-100 rounded-full flex items-center justify-center mr-3">
                              <Shield className="w-5 h-5 text-blue-600" />
                            </div>
                            <div>
                              <div className="flex items-center gap-2 mb-1">
                                <span
                                  className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getEventTypeColor(
                                    report.event_type
                                  )}`}
                                >
                                  {report.event_type.replace(/_/g, " ")}
                                </span>
                              </div>
                              <div className="text-sm text-gray-600 truncate max-w-xs">
                                {report.event_description}
                              </div>
                            </div>
                          </div>
                        </td>

                        {/* User */}
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {typeof report.action_by_info === "object" && report.action_by_info?.userName
                              ? report.action_by_info.userName
                              : typeof report.action_by_info === "string"
                                ? report.action_by_info
                                : "System"}
                          </div>
                          <div className="text-sm text-gray-500 font-mono">
                            {report.user_id
                              ? `${report.user_id.substring(0, 8)}...`
                              : "N/A"}
                          </div>
                        </td>

                        {/* Time */}
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center text-sm text-gray-900">
                            <Calendar className="w-4 h-4 mr-2 text-gray-400" />
                            {formatDate(report.event_time).toString()}
                          </div>
                        </td>

                        {/* IP Address */}
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center text-sm text-gray-900">
                            <Globe className="w-4 h-4 mr-2 text-gray-400" />
                            {report.meta_data?.ip_address || (report as any).ip_address || report.meta_data?.ip || "N/A"}
                          </div>
                        </td>

                        {/* Device */}
                        <td className="px-6 py-4 whitespace-nowrap">
                          {report.meta_data?.user_agent ? (() => {
                            const ua = report.meta_data.user_agent;
                            const lowerUa = ua.toLowerCase();
                            const isMobile = /mobile|android|iphone|ipad|phone/i.test(lowerUa);
                            
                            let browser = "Browser";
                            if (lowerUa.includes("firefox")) {
                              browser = "Firefox";
                            } else if (lowerUa.includes("chrome") && !lowerUa.includes("chromium")) {
                              browser = "Chrome";
                            } else if (lowerUa.includes("safari") && !lowerUa.includes("chrome")) {
                              browser = "Safari";
                            } else if (lowerUa.includes("edge") || lowerUa.includes("edg/")) {
                              browser = "Edge";
                            } else if (lowerUa.includes("opera") || lowerUa.includes("opr/")) {
                              browser = "Opera";
                            }

                            let os = "Unknown OS";
                            if (lowerUa.includes("windows")) {
                              os = "Windows";
                            } else if (lowerUa.includes("macintosh") || lowerUa.includes("mac os")) {
                              os = "macOS";
                            } else if (lowerUa.includes("linux")) {
                              os = "Linux";
                            } else if (lowerUa.includes("android")) {
                              os = "Android";
                            } else if (lowerUa.includes("iphone") || lowerUa.includes("ipad")) {
                              os = "iOS";
                            }

                            return (
                              <div className="flex items-center text-sm text-gray-900">
                                {isMobile ? (
                                  <Smartphone className="w-4 h-4 mr-2 text-gray-400" />
                                ) : (
                                  <Monitor className="w-4 h-4 mr-2 text-gray-400" />
                                )}
                                <div>
                                  <div>{browser}</div>
                                  <div className="text-xs text-gray-500">{os}</div>
                                </div>
                              </div>
                            );
                          })() : (
                            <div className="flex items-center text-sm text-gray-500">
                              <Monitor className="w-4 h-4 mr-2 text-gray-400" />
                              Unknown
                            </div>
                          )}
                        </td>

                        {/* Actions */}
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <button
                            onClick={() => handleViewDetails(report)}
                            className="text-blue-600 hover:text-blue-900 p-1 rounded"
                          >
                            <Tooltip content="View">
                              <Eye className="w-4 h-4" />
                            </Tooltip>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Pagination */}
            <div className="flex items-center justify-between mb-6">
              <div className="text-sm text-gray-700">
                Showing <span className="font-medium">{startItem}</span> to{" "}
                <span className="font-medium">{endItem}</span> of{" "}
                <span className="font-medium">{totalCount}</span> logs
              </div>
              {totalPages > 1 && (
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>

                  {/* Page Numbers */}
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }

                    return (
                      <button
                        key={pageNum}
                        onClick={() => handlePageChange(pageNum)}
                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${currentPage === pageNum
                            ? "z-10 bg-blue-50 border-blue-500 text-blue-600"
                            : "bg-white border-gray-300 text-gray-500 hover:bg-gray-50"
                          }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}

                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>
          </>
        ) : (
          <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
            <Shield className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              No user activity logs found
            </h3>
            <p className="text-gray-500 mb-6">
              No events match your current search criteria. Try adjusting your filters.
            </p>
          </div>
        )}
      </div>

      {/* Log Inspector Detail Modal */}
      {showDetailModal && selectedReport && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col animate-in zoom-in-95 duration-200">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-100 to-purple-100 rounded-full flex items-center justify-center">
                  <Eye className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">
                    Log Details
                  </h2>
                  <div className="flex items-center gap-2 mt-1">
                    <span
                      className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getEventTypeColor(
                        selectedReport.event_type
                      )}`}
                    >
                      {selectedReport.event_type.replace(/_/g, " ")}
                    </span>
                    <span className="text-sm text-gray-500">
                      {formatDate(selectedReport.event_time)}
                    </span>
                  </div>
                </div>
              </div>
              <button
                onClick={handleCloseDetail}
                className="text-gray-400 hover:text-gray-600 p-1 rounded-lg"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Tabs */}
            <div className="flex border-b border-gray-200 px-6">
              {[
                { id: "overview", label: "Overview", icon: Info },
                { id: "metadata", label: "Metadata", icon: Code2 },
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as any)}
                  className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${activeTab === tab.id
                      ? "border-blue-500 text-blue-600"
                      : "border-transparent text-gray-500 hover:text-gray-700"
                    }`}
                >
                  <tab.icon className="w-4 h-4" />
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Content */}
            <div className="flex-1 overflow-auto p-6">
              {activeTab === "overview" && (
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Event Information */}
                    <div className="space-y-4">
                      <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                        Event Information
                      </h3>
                      <div className="space-y-4">
                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-blue-50 text-blue-600 rounded-lg">
                            <Calendar className="w-5 h-5" />
                          </div>
                          <div>
                            <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                              Event Time
                            </div>
                            <div className="text-sm font-medium text-gray-900 mt-0.5">
                              {formatDate(selectedReport.event_time)}
                            </div>
                          </div>
                        </div>

                        <div className="flex items-start gap-3">
                          <div className="p-2 bg-sky-50 text-sky-600 rounded-lg mt-0.5">
                            <Info className="w-5 h-5" />
                          </div>
                          <div>
                            <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                              Activity Description
                            </div>
                            <div className="text-sm font-medium text-gray-900 mt-0.5 break-words">
                              {selectedReport.event_description}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Performer / User Information */}
                    <div className="space-y-4">
                      <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                        User & Actor Details
                      </h3>
                      <div className="space-y-4">
                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-purple-50 text-purple-600 rounded-lg">
                            <User className="w-5 h-5" />
                          </div>
                          <div>
                            <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                              Target User ID
                            </div>
                            <div className="text-sm font-medium text-gray-900 font-mono mt-0.5 break-all">
                              {selectedReport.user_id || "N/A"}
                            </div>
                          </div>
                        </div>

                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-indigo-50 text-indigo-600 rounded-lg">
                            <User className="w-5 h-5" />
                          </div>
                          <div>
                            <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                              Performed By (Username)
                            </div>
                            <div className="text-sm font-medium text-gray-900 mt-0.5">
                              {typeof selectedReport.action_by_info === "object" &&
                                selectedReport.action_by_info?.userName
                                ? selectedReport.action_by_info.userName
                                : typeof selectedReport.action_by_info === "string"
                                  ? selectedReport.action_by_info
                                  : "System"}
                            </div>
                          </div>
                        </div>

                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-violet-50 text-violet-600 rounded-lg">
                            <User className="w-5 h-5" />
                          </div>
                          <div>
                            <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                              Actor User ID
                            </div>
                            <div className="text-sm font-medium text-gray-900 font-mono mt-0.5 break-all">
                              {selectedReport.action_by || "N/A"}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Security & Network Details */}
                  {(selectedReport.meta_data?.ip_address || (selectedReport as any).ip_address || selectedReport.meta_data?.ip || selectedReport.meta_data?.user_agent) && (
                    <div className="border-t border-gray-100 pt-6 space-y-4">
                      <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                        Security & Network Details
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* IP Address */}
                        {(selectedReport.meta_data?.ip_address || (selectedReport as any).ip_address || selectedReport.meta_data?.ip) && (
                          <div className="flex items-center gap-3">
                            <div className="p-2 bg-emerald-50 text-emerald-600 rounded-lg">
                              <Globe className="w-5 h-5" />
                            </div>
                            <div>
                              <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                IP Address
                              </div>
                              <div className="text-sm font-medium text-gray-900 font-mono mt-0.5">
                                {selectedReport.meta_data?.ip_address || (selectedReport as any).ip_address || selectedReport.meta_data?.ip}
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Device / Browser */}
                        {selectedReport.meta_data?.user_agent && (() => {
                          const ua = selectedReport.meta_data.user_agent;
                          const lowerUa = ua.toLowerCase();
                          const isMobile = /mobile|android|iphone|ipad|phone/i.test(lowerUa);
                          
                          let browser = "Browser";
                          if (lowerUa.includes("firefox")) {
                            browser = "Firefox";
                          } else if (lowerUa.includes("chrome") && !lowerUa.includes("chromium")) {
                            browser = "Chrome";
                          } else if (lowerUa.includes("safari") && !lowerUa.includes("chrome")) {
                            browser = "Safari";
                          } else if (lowerUa.includes("edge") || lowerUa.includes("edg/")) {
                            browser = "Edge";
                          } else if (lowerUa.includes("opera") || lowerUa.includes("opr/")) {
                            browser = "Opera";
                          } else if (lowerUa.includes("trident") || lowerUa.includes("msie")) {
                            browser = "IE";
                          }

                          let os = "Unknown OS";
                          if (lowerUa.includes("windows")) {
                            os = "Windows";
                          } else if (lowerUa.includes("macintosh") || lowerUa.includes("mac os")) {
                            os = "macOS";
                          } else if (lowerUa.includes("linux")) {
                            os = "Linux";
                          } else if (lowerUa.includes("android")) {
                            os = "Android";
                          } else if (lowerUa.includes("iphone") || lowerUa.includes("ipad")) {
                            os = "iOS";
                          }

                          const deviceText = isMobile ? `Mobile Device (${browser} on ${os})` : `${browser} Browser on ${os}`;

                          return (
                            <div className="flex items-center gap-3">
                              <div className="p-2 bg-amber-50 text-amber-600 rounded-lg">
                                {isMobile ? (
                                  <Smartphone className="w-5 h-5" />
                                ) : (
                                  <Monitor className="w-5 h-5" />
                                )}
                              </div>
                              <div>
                                <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                  Device & Browser
                                </div>
                                <div className="text-sm font-medium text-gray-900 mt-0.5">
                                  {deviceText}
                                </div>
                              </div>
                            </div>
                          );
                        })()}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {activeTab === "metadata" && (
                <div className="space-y-4">
                  <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
                    <div className="w-3 h-3 bg-blue-200 rounded-full"></div>
                    Metadata (JSON Context)
                  </div>
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 max-h-96 overflow-auto relative">
                    <pre className="text-sm text-gray-800 whitespace-pre-wrap font-mono">
                      {JSON.stringify(selectedReport.meta_data || {}, null, 2)}
                    </pre>
                  </div>
                  <div className="flex items-center gap-2 pt-4 border-t border-gray-200">
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(
                          JSON.stringify(selectedReport.meta_data || {}, null, 2)
                        );
                        setCopied(true);
                        setTimeout(() => setCopied(false), 2000);
                      }}
                      className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
                    >
                      {copied ? (
                        <CheckCircle className="w-3 h-3" />
                      ) : (
                        <Copy className="w-3 h-3" />
                      )}
                      {copied ? "Copied!" : "Copy to Clipboard"}
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex justify-end gap-3 p-6 border-t border-gray-200 bg-gray-50">
              <button
                onClick={handleCloseDetail}
                className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserActivityReports;
